import React, {useState} from 'react';
import {toast} from 'sonner';
import {network} from '../lib/network';
import {UtilitySchemas} from '@plunk/shared';
import {NextSeo} from 'next-seo';
import {Footer, Navbar} from '../components';
import {AnimatePresence, motion} from 'framer-motion';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {Check, X} from 'lucide-react';

interface Verification {
  valid: boolean;
  validators: {
    typo: {
      valid: boolean;
      reason: string;
    };
    disposable: {
      valid: boolean;
    };
    mx: {
      valid: boolean;
    };
    smtp: {
      valid: boolean;
    };
  };
}

/**
 *
 */
export default function Index() {
  const [result, setResult] = useState<Verification>();

  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
    watch,
  } = useForm<{
    email: string;
  }>({
    resolver: zodResolver(UtilitySchemas.email),
  });

  const validate = async (data: {email: string}) => {
    await toast.promise(network.fetch<Verification, typeof UtilitySchemas.email>('POST', '/utils/validate', data), {
      loading: 'Validating email',
      success: result => {
        setResult(result);
        return 'Validated email';
      },
      error: 'Could not validate email',
    });
  };

  return (
    <>
      <NextSeo
        title={'Email Validator | The Open-Source Email Platform'}
        description={'Validate an email address to see if it is valid, disposable, and more.'}
        openGraph={{
          title: 'Email Validator | The Open-Source Email Platform',
          description: 'Validate an email address to see if it is valid, disposable, and more.',
        }}
        additionalMetaTags={[
          {
            property: 'title',
            content: 'Email Validator | The Open-Source Email Platform',
          },
        ]}
      />
      <Navbar />

      <div className={'mx-auto max-w-7xl px-8 sm:px-0'}>
        <main>
          <div className={'mb-4 flex flex-col items-center justify-center gap-y-2'}>
            <div className={'mt-6 text-center'}>
              <h1 className="text-4xl font-bold text-neutral-800">Email Validator</h1>
              <p className="mx-auto mt-2 max-w-lg text-neutral-500">
                Validate an email address to see if it is valid, disposable, and more.
              </p>
            </div>
          </div>

          <div className={'mx-auto w-full max-w-3xl p-6'}>
            {result ? (
              <>
                <h2 className={'mb-6 text-center text-xl font-bold text-neutral-800 md:text-2xl'}>
                  {watch('email')[0].toUpperCase()}
                  {watch('email').slice(1)} <span className={'block'}>is {result.valid ? 'valid' : 'invalid'}</span>
                </h2>
                <div className={'grid gap-6 sm:grid-cols-2'}>
                  <div className={'rounded-md border border-neutral-200 p-6'}>
                    {result.validators.disposable.valid ? (
                      <Check className={'h-10 w-10 rounded-full bg-green-50 p-2 text-green-500'} strokeWidth={3} />
                    ) : (
                      <X className={'h-10 w-10 rounded-full bg-red-50 p-2 text-red-500'} strokeWidth={3} />
                    )}
                    <h3 className={'mt-4 text-lg font-semibold text-neutral-800'}>Disposable</h3>
                    <p className={'mt-2 text-sm text-neutral-500'}>
                      {result.validators.disposable.valid
                        ? 'Domain is not linked to a disposable email provider'
                        : 'Email domain is linked to a disposable email provider'}
                    </p>
                  </div>
                  <div className={'rounded-md border border-neutral-200 p-6'}>
                    {result.validators.mx.valid ? (
                      <Check className={'h-10 w-10 rounded-full bg-green-50 p-2 text-green-500'} strokeWidth={3} />
                    ) : (
                      <X className={'h-10 w-10 rounded-full bg-red-50 p-2 text-red-500'} strokeWidth={3} />
                    )}
                    <h3 className={'mt-4 text-lg font-semibold text-neutral-800'}>MX</h3>
                    <p className={'mt-2 text-sm text-neutral-500'}>
                      {result.validators.mx.valid
                        ? 'MX records exist for domain'
                        : 'MX records do not exist for domain'}
                    </p>
                  </div>
                  <div className={'rounded-md border border-neutral-200 p-6'}>
                    {result.validators.typo.valid ? (
                      <Check className={'h-10 w-10 rounded-full bg-green-50 p-2 text-green-500'} strokeWidth={3} />
                    ) : (
                      <X className={'h-10 w-10 rounded-full bg-red-50 p-2 text-red-500'} strokeWidth={3} />
                    )}
                    <h3 className={'mt-4 text-lg font-semibold text-neutral-800'}>Typo</h3>
                    <p className={'mt-2 text-sm text-neutral-500'}>
                      {result.validators.typo.valid
                        ? 'Email is not a common typo'
                        : `Did you mean ${result.validators.typo.reason.split('@')[1]}?`}
                    </p>
                  </div>
                  <div className={'rounded-md border border-neutral-200 p-6'}>
                    {result.validators.smtp.valid ? (
                      <Check className={'h-10 w-10 rounded-full bg-green-50 p-2 text-green-500'} strokeWidth={3} />
                    ) : (
                      <X className={'h-10 w-10 rounded-full bg-yellow-100 p-2 text-yellow-600'} strokeWidth={3} />
                    )}
                    <h3 className={'mt-4 text-lg font-semibold text-neutral-800'}>SMTP</h3>
                    <p className={'mt-2 text-sm text-neutral-500'}>
                      {result.validators.smtp.valid
                        ? 'SMTP connection verified'
                        : `Could not verify SMTP connection. Some email providers block direct SMTP connections.`}
                    </p>
                  </div>
                  <div className={'rounded-md border border-neutral-200 p-6'}>
                    {!watch('email').includes('+') ? (
                      <Check className={'h-10 w-10 rounded-full bg-green-50 p-2 text-green-500'} strokeWidth={3} />
                    ) : (
                      <X className={'h-10 w-10 rounded-full bg-yellow-100 p-2 text-yellow-600'} strokeWidth={3} />
                    )}
                    <h3 className={'mt-4 text-lg font-semibold text-neutral-800'}>Plus Addressing</h3>
                    <p className={'mt-2 text-sm text-neutral-500'}>
                      {!watch('email').includes('+')
                        ? 'Email does not contain a plus address'
                        : `Email contains a plus address.`}
                    </p>
                  </div>
                </div>
                <div className={'mt-9 flex items-center justify-center'}>
                  <motion.button
                    onClick={() => {
                      reset();
                      setResult(undefined);
                    }}
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.9}}
                    className={'h-full rounded-lg bg-neutral-800 px-12 py-3 text-sm font-semibold text-white'}
                  >
                    Validate another
                  </motion.button>
                </div>
              </>
            ) : (
              <>
                <form onSubmit={handleSubmit(validate)} className="space-y-6">
                  <div>
                    <label htmlFor={'email'} className="block text-sm font-medium text-neutral-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <input
                        type={'email'}
                        className={
                          'block w-full rounded border-neutral-300 transition ease-in-out focus:border-neutral-800 focus:ring-neutral-800 sm:text-sm'
                        }
                        autoComplete={'email'}
                        placeholder={'hello@useplunk.com'}
                        {...register('email')}
                      />
                    </div>
                    <AnimatePresence>
                      {errors.email?.message && (
                        <motion.p
                          initial={{height: 0}}
                          animate={{height: 'auto'}}
                          exit={{height: 0}}
                          className="mt-1 text-xs text-red-500"
                        >
                          {errors.email.message}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className={'mt-9 flex items-center justify-center'}>
                    <motion.button
                      type={'submit'}
                      whileHover={{scale: 1.05}}
                      whileTap={{scale: 0.9}}
                      className={'h-full rounded-lg bg-neutral-800 px-12 py-3 text-sm font-semibold text-white'}
                    >
                      Validate
                    </motion.button>
                  </div>
                </form>
              </>
            )}
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
}
